import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export const PORTAL_SHELL_STYLE = {
  position: "fixed",
  zIndex: 99999,
  width: 0,
  height: 0,
  overflow: "visible",
  pointerEvents: "none",
  background: "transparent",
  backgroundColor: "transparent",
};

export default function useFixedPortalMenu({
  open,
  onClose,
  triggerRef,
  menuRef,
  minHeight = 160,
  maxHeightCap = 420,
}) {
  const [menuStyle, setMenuStyle] = useState({});
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const width = Math.min(Math.max(rect.width, 0), Math.max(0, vw - pad * 2));
    let left = rect.left;
    if (left + width > vw - pad) left = Math.max(pad, vw - pad - width);
    if (left < pad) left = pad;

    const cap = Math.min(maxHeightCap, vh * 0.62);
    const spaceBelow = vh - rect.bottom - gap - pad;
    const spaceAbove = rect.top - gap - pad;
    const preferUp = spaceBelow < minHeight && spaceAbove > spaceBelow;

    let maxHeight;
    let top;
    if (preferUp) {
      maxHeight = Math.min(cap, Math.max(minHeight, spaceAbove));
      top = Math.max(pad, rect.top - gap - maxHeight);
    } else {
      maxHeight = Math.min(cap, Math.max(minHeight, spaceBelow));
      top = rect.bottom + gap;
      if (top + Math.min(maxHeight, minHeight) > vh - pad) {
        maxHeight = Math.min(cap, Math.max(120, spaceAbove));
        top = Math.max(pad, rect.top - gap - maxHeight);
      }
    }

    setMenuStyle({
      position: "fixed",
      top,
      left,
      width,
      maxHeight,
      zIndex: 99999,
      pointerEvents: "auto",
    });
  }, [triggerRef, minHeight, maxHeightCap]);

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = () => updatePosition();
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onCloseRef.current?.();
    };
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, updatePosition, triggerRef, menuRef]);

  return { menuStyle, updatePosition };
}
